/**
 * Comparaison de deux contenus de code, en vue côte à côte ou unifiée (§3.5).
 *
 * La première version de ce composant se contentait d'afficher deux colonnes
 * numérotées et de griser les lignes présentes de part et d'autre : ce n'était
 * pas un diff. Le calcul réel vit désormais dans `lib/diff.ts` (algorithme de
 * Myers via `diffLines`), ce composant ne fait que le peindre — d'où sa forme :
 * aucune logique de comparaison ici, uniquement de la mise en page.
 *
 * Il sert aussi bien à comparer deux versions d'une application (fiche
 * utilisateur) qu'à comparer le code original et le code transformé d'un même
 * déploiement (fiche d'administration) : dans les deux cas, deux textes.
 */

import clsx from 'clsx';
import { Columns2, Copy, Rows3 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useClipboard } from '../hooks/useClipboard';
import { useHighlightedCode } from '../hooks/useHighlightedCode';
import {
  buildDiffRows,
  formatDiffSummary,
  isIdenticalSummary,
  pairDiffRows,
  summarizeDiff,
  type DiffRow,
} from '../lib/diff';
import { formatNumber } from '../lib/format';
import { Button, CopyFeedback, EmptyState } from './ui';

/** Mode d'affichage du comparatif. */
export type DiffMode = 'split' | 'unified';

/**
 * Nombre maximal de rangées rendues.
 *
 * Un artifact peut peser jusqu'à 1 Mo, soit plusieurs dizaines de milliers de
 * lignes : les peindre toutes fige l'onglet. Au-delà de cette limite, le
 * comparatif est tronqué et le dit — le résumé, lui, reste calculé sur la
 * totalité du fichier.
 */
export const MAX_RENDERED_ROWS = 2500;

/** Largeur des gouttières de numérotation. */
const GUTTER = 'w-12 shrink-0 select-none bg-slate-900 px-2 text-right text-slate-600';

/** Fonds appliqués aux lignes selon leur nature. */
const ROW_TONES: Record<DiffRow['kind'], string> = {
  added: 'bg-emerald-500/15 text-emerald-100',
  removed: 'bg-red-500/15 text-red-100',
  unchanged: 'text-slate-300',
};

/** Marqueur textuel, seul repère lisible en cas de daltonisme. */
const ROW_MARKS: Record<DiffRow['kind'], string> = {
  added: '+',
  removed: '−',
  unchanged: ' ',
};

/**
 * Bouton de copie d'un côté du comparatif.
 *
 * Le libellé porte le nom de la version : deux boutons marqués « Copier » côte
 * à côte n'indiquent pas lequel copie quoi, et un `aria-label` distinct ne
 * profite qu'aux lecteurs d'écran.
 */
function CopyButton({ version, content }: { version: string; content: string }) {
  const { status, copy } = useClipboard();
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <CopyFeedback status={status} />
      <Button variant="ghost" onClick={() => copy(content)} aria-label={`Copier ${version}`}>
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        Copier {version}
      </Button>
    </span>
  );
}

/**
 * Coloration d'un côté du comparatif, indexée par numéro de ligne.
 *
 * Les tableaux sont ceux produits par `splitHighlightedLines` : une entrée par
 * ligne du document d'origine, ce qui permet de retrouver la coloration d'une
 * ligne du diff sans la recoloriser hors de son contexte — un bloc CSS ou une
 * chaîne sur plusieurs lignes ne se colore correctement que si l'on a vu ce qui
 * précède.
 */
interface HighlightedSides {
  base: string[] | null;
  target: string[] | null;
}

/**
 * Balisage colorisé d'une ligne du comparatif, ou `null` à défaut.
 *
 * Le côté consulté suit la nature de la ligne : une ligne ajoutée n'existe que
 * dans la cible, une ligne supprimée que dans la référence, et une ligne
 * inchangée a le même contenu des deux côtés.
 */
function lineHtml(row: DiffRow, sides: HighlightedSides, side?: 'base' | 'target'): string | null {
  const cible = side ?? (row.kind === 'added' ? 'target' : 'base');
  const numero = cible === 'base' ? row.baseLineNumber : row.targetLineNumber;
  if (numero === null) return null;
  const html = sides[cible]?.[numero - 1];
  return html === undefined ? null : html;
}

/**
 * Contenu d'une ligne : colorisé si le moteur l'a fourni, texte brut sinon.
 *
 * Le repli n'est pas un détail de confort : c'est ce qui garantit que le
 * comparatif reste lisible avant le chargement du moteur, au-delà de la limite de
 * volume, et si le morceau ne se télécharge pas.
 */
function LineContent({ content, html }: { content: string; html: string | null }) {
  if (html !== null) {
    return (
      <code
        className="whitespace-pre-wrap break-all px-2"
        // Balisage produit par `highlight.js`, qui échappe son entrée : le code
        // d'un utilisateur n'en ressort jamais sous forme de balises.
        dangerouslySetInnerHTML={{ __html: html === '' ? ' ' : html }}
      />
    );
  }
  return <code className="whitespace-pre-wrap break-all px-2">{content || ' '}</code>;
}

/**
 * Une ligne de la vue unifiée : les numéros, le marqueur, le contenu.
 *
 * Les deux numéros — avant et après — sont ce qui permet de situer une ligne
 * dans chaque version. Quand les contenus sont identiques ils coïncident
 * toujours : `singleGutter` n'en affiche alors qu'un, la seconde colonne ne
 * répétant que le même chiffre.
 */
function UnifiedRow({
  row,
  sides,
  singleGutter,
}: {
  row: DiffRow;
  sides: HighlightedSides;
  singleGutter?: boolean;
}) {
  return (
    <div className={clsx('flex', ROW_TONES[row.kind])}>
      <span className={clsx(GUTTER, 'w-10')}>{row.baseLineNumber ?? ''}</span>
      {singleGutter ? null : (
        <span className={clsx(GUTTER, 'w-10')}>{row.targetLineNumber ?? ''}</span>
      )}
      {singleGutter ? null : (
        <span aria-hidden="true" className="w-4 shrink-0 select-none text-center opacity-70">
          {ROW_MARKS[row.kind]}
        </span>
      )}
      <LineContent content={row.content} html={lineHtml(row, sides)} />
    </div>
  );
}

/** Un demi-rang de la vue côte à côte, éventuellement vide. */
function SplitCell({
  row,
  side,
  sides,
}: {
  row: DiffRow | null;
  side: 'base' | 'target';
  sides: HighlightedSides;
}) {
  if (!row) {
    return (
      <div className="flex min-w-0 flex-1 bg-slate-900/40">
        <span className={GUTTER} />
        <code className="whitespace-pre-wrap break-all px-2" />
      </div>
    );
  }

  return (
    <div className={clsx('flex min-w-0 flex-1', ROW_TONES[row.kind])}>
      <span className={GUTTER}>
        {(side === 'base' ? row.baseLineNumber : row.targetLineNumber) ?? ''}
      </span>
      <LineContent content={row.content} html={lineHtml(row, sides, side)} />
    </div>
  );
}

/** Propriétés du comparatif. */
export interface CodeDiffProps {
  /** Contenu le plus ancien : côté gauche. */
  baseCode: string | null;
  /** Contenu le plus récent : côté droit. */
  targetCode: string | null;
  baseLabel: string;
  targetLabel: string;
  /** Mode initial ; l'utilisateur peut ensuite basculer. */
  defaultMode?: DiffMode;
  /**
   * Égalité affirmée par l'API (`VersionDiffDto.identical`). Sans elle, elle
   * est déduite du calcul local, qui aboutit au même verdict.
   */
  identical?: boolean;
  /** Phrase affichée lorsque les deux contenus coïncident. */
  identicalMessage?: string;
}

/** Comparatif de deux contenus, en vue côte à côte ou unifiée. */
export function CodeDiff({
  baseCode,
  targetCode,
  baseLabel,
  targetLabel,
  defaultMode = 'split',
  identical,
  identicalMessage,
}: CodeDiffProps) {
  const [mode, setMode] = useState<DiffMode>(defaultMode);

  const base = baseCode ?? '';
  const target = targetCode ?? '';

  // Le calcul est mémorisé : il est linéaire en nombre de lignes, mais il n'y a
  // aucune raison de le refaire à chaque bascule de mode ou survol de bouton.
  const rows = useMemo(() => buildDiffRows(base, target), [base, target]);
  const pairs = useMemo(() => pairDiffRows(rows), [rows]);
  const summary = useMemo(() => summarizeDiff(rows), [rows]);

  // Chaque côté est colorisé **en entier**, une seule fois, puis consulté ligne
  // par ligne. Coloriser chaque ligne isolément serait plus simple et faux : un
  // bloc CSS ou une chaîne sur plusieurs lignes ne se colore correctement que si
  // l'on a vu ce qui précède.
  const baseHighlight = useHighlightedCode(base);
  const targetHighlight = useHighlightedCode(target);
  const sides = useMemo<HighlightedSides>(
    () => ({
      base: baseHighlight.result?.lines ?? null,
      target: targetHighlight.result?.lines ?? null,
    }),
    [baseHighlight.result, targetHighlight.result],
  );

  if (baseCode === null && targetCode === null) {
    return (
      <EmptyState
        title="Aucun code conservé"
        description="Le code original est enregistré à la création du job ; le code transformé n'apparaît qu'une fois l'étape d'adaptation par l'IA terminée."
      />
    );
  }

  const same = identical ?? isIdenticalSummary(summary);
  const shownRows = rows.slice(0, MAX_RENDERED_ROWS);
  const shownPairs = pairs.slice(0, MAX_RENDERED_ROWS);
  const truncated =
    mode === 'split' ? pairs.length > shownPairs.length : rows.length > shownRows.length;

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {/* « V1 → V1 » suggère une comparaison qui n'a pas lieu. */}
            {same ? baseLabel : `${baseLabel} → ${targetLabel}`}
          </p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {same ? (
              <>
                {formatNumber(summary.unchanged)} ligne{summary.unchanged > 1 ? 's' : ''}
              </>
            ) : (
              <>
                {formatDiffSummary(summary)} · {formatNumber(summary.unchanged)} inchangée
                {summary.unchanged > 1 ? 's' : ''}
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Deux contenus identiques n'ont ni côtés à confronter ni versions
              distinctes à copier : proposer le choix afficherait un bouton
              sélectionné sans effet, et deux boutons copiant le même texte. */}
          {same ? (
            <CopyButton version="le code" content={base} />
          ) : (
            <>
              <div className="flex items-center gap-1" role="group" aria-label="Mode d'affichage">
                <Button
                  variant={mode === 'split' ? 'primary' : 'secondary'}
                  aria-pressed={mode === 'split'}
                  onClick={() => setMode('split')}
                >
                  <Columns2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Côte à côte
                </Button>
                <Button
                  variant={mode === 'unified' ? 'primary' : 'secondary'}
                  aria-pressed={mode === 'unified'}
                  onClick={() => setMode('unified')}
                >
                  <Rows3 className="h-3.5 w-3.5" aria-hidden="true" />
                  Unifié
                </Button>
              </div>
              <CopyButton version={baseLabel} content={base} />
              <CopyButton version={targetLabel} content={target} />
            </>
          )}
        </div>
      </div>

      {same ? (
        <p
          role="status"
          className="border-b border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300"
        >
          {identicalMessage ??
            'Les deux contenus sont rigoureusement identiques : il n’y a aucune différence à afficher.'}{' '}
          Le code est reproduit ci-dessous à titre de consultation.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Les deux contenus sont vides.
        </p>
      ) : (
        <div className="code-diff max-h-[36rem] overflow-auto bg-slate-950 font-mono text-xs leading-5">
          {mode === 'split' && !same ? (
            <>
              <div className="sticky top-0 z-10 flex bg-slate-900 text-[0.7rem] uppercase tracking-wide text-slate-400">
                <span className="min-w-0 flex-1 px-3 py-1">{baseLabel}</span>
                <span className="min-w-0 flex-1 border-l border-slate-800 px-3 py-1">
                  {targetLabel}
                </span>
              </div>
              {shownPairs.map((pair, index) => (
                <div key={index} className="flex divide-x divide-slate-800">
                  <SplitCell row={pair.left} side="base" sides={sides} />
                  <SplitCell row={pair.right} side="target" sides={sides} />
                </div>
              ))}
            </>
          ) : (
            shownRows.map((row, index) => (
              <UnifiedRow key={index} row={row} sides={sides} singleGutter={same} />
            ))
          )}
        </div>
      )}

      {truncated ? (
        <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
          Affichage limité aux {formatNumber(MAX_RENDERED_ROWS)} premières lignes du comparatif ; le
          résumé ci-dessus porte, lui, sur la totalité du fichier.
        </p>
      ) : null}
    </div>
  );
}
